import { z } from 'zod'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { RouteRepository } from '../../application/RouteRepository'
import { createSupabaseRoutePlanning } from './supabaseRoutePlanning'

interface SupabaseResponse {
  data: unknown
  error: { code?: string; message?: string } | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

const routeRowSchema = z.object({
  id: z.uuid(),
  nom: z.string().nullable(),
  couleur: z.string().nullable(),
})

// Grain = adresse de service, pas contrat : un contrat à 2 adresses produit 2
// lignes, chacune avec le même `numero` de contrat en référence.
const serviceLocationRowSchema = z.object({
  id: z.uuid(),
  ordre: z.number().int(),
  formatted_address: z.string(),
  latitude: z.coerce.number().nullable(),
  longitude: z.coerce.number().nullable(),
  contrat: z.object({
    id: z.uuid(),
    numero: z.string().nullable(),
    statut: z.string(),
    client: z
      .object({
        prenom: z.string().nullable(),
        nom: z.string().nullable(),
        entreprise: z.string().nullable(),
      })
      .nullable(),
  }),
})

function clientDisplayName(client: {
  prenom: string | null
  nom: string | null
  entreprise: string | null
}): string {
  const company = client.entreprise?.trim()
  if (company) return company
  return [client.prenom, client.nom].filter(Boolean).join(' ').trim() || 'Sans nom'
}

const assignableStatuses = ['actif', 'suspendu']

export function createSupabaseRouteRepository(client: SupabaseClient): RouteRepository {
  return {
    ...createSupabaseRoutePlanning(client),
    async listActiveRoutes() {
      const response = asResponse(
        await client.from('routes').select('id,nom,couleur').is('deleted_at', null).order('nom'),
      )
      if (response.error) throw new Error(`ROUTE_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const parsed = z.array(routeRowSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('ROUTE_LIST_RESPONSE_INVALID')
      return {
        routes: parsed.data.map((row) => ({
          id: row.id,
          nom: row.nom ?? 'Route sans nom',
          couleur: row.couleur ?? '#14304f',
        })),
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      }
    },
    async listUnassignedServiceLocations() {
      // Anti-jointure en 2 requêtes (PostgREST ne fait pas de NOT IN sur une
      // jointure) : d'abord les adresses déjà activement routées, puis les
      // adresses de contrats actifs/suspendus qui n'en font pas partie — même
      // patron que l'ancien reca-app (unassignedContracts.service.ts).
      const [rawAssigned, rawLocations] = await Promise.all([
        client.from('route_contracts').select('contract_service_location_id').is('deleted_at', null),
        client
          .from('contract_service_locations')
          .select(
            'id,ordre,formatted_address,latitude,longitude,' +
              'contrat:contracts(id,numero,statut,client:clients(prenom,nom,entreprise))',
          )
          // Une adresse retirée du Contrat ne se route plus (24 oct. 2026).
          .neq('service_status', 'REMOVED')
          .order('ordre'),
      ])
      const assignedResponse = asResponse(rawAssigned)
      const locationsResponse = asResponse(rawLocations)
      if (assignedResponse.error) {
        throw new Error(`ROUTE_ASSIGNED_LOCATIONS_FAILED:${assignedResponse.error.code ?? 'UNKNOWN'}`)
      }
      if (locationsResponse.error) {
        throw new Error(`ROUTE_UNASSIGNED_LOCATIONS_FAILED:${locationsResponse.error.code ?? 'UNKNOWN'}`)
      }
      const assignedIds = new Set(
        (assignedResponse.data as { contract_service_location_id: string }[]).map(
          (row) => row.contract_service_location_id,
        ),
      )
      const parsedLocations = z.array(serviceLocationRowSchema).safeParse(locationsResponse.data)
      if (!parsedLocations.success) throw new Error('ROUTE_UNASSIGNED_LOCATIONS_RESPONSE_INVALID')

      const locations = parsedLocations.data
        .filter((row) => assignableStatuses.includes(row.contrat.statut) && !assignedIds.has(row.id))
        .map((row) => ({
          contractId: row.contrat.id,
          contractNumber: row.contrat.numero ?? '—',
          clientDisplayName: row.contrat.client ? clientDisplayName(row.contrat.client) : 'Sans nom',
          serviceLocationId: row.id,
          formattedAddress: row.formatted_address,
          ...(row.latitude !== null ? { latitude: row.latitude } : {}),
          ...(row.longitude !== null ? { longitude: row.longitude } : {}),
          ordre: row.ordre,
        }))

      return {
        locations,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      }
    },
    async assignServiceLocationToRoute(serviceLocationId, routeId) {
      const response = asResponse(
        await client.rpc('assign_service_location_to_route_v1', {
          p_contract_service_location_id: serviceLocationId,
          p_route_id: routeId,
        }),
      )
      if (response.error) {
        throw new Error(response.error.message ?? `ROUTE_ASSIGN_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      }
    },
    async generateMissionFromRoute(input) {
      const response = asResponse(
        await client.rpc('generate_mission_from_route_v1', {
          p_route_id: input.routeId,
          p_date: input.date,
          p_heure_prevue: input.heurePrevue,
          p_operator_id: input.operatorId,
          p_equipment_id: input.equipmentId,
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message ?? `ROUTE_GENERATE_MISSION_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      return { missionId: response.data as string }
    },
  }
}
